import React, {ReactNode} from "react";
import {Button} from "primereact/button";
import Exception from "../../../../shared/exception/Exception";
import MessageBox from "../../../../shared/components/MessageBox";
import {Dialog} from "primereact/dialog";
import {Password} from "primereact/password";
import PortalDocument from "../../../document/PortalDocument";
import ErrorCode from "../../../../shared/exception/ErrorCode";
import errorCode from "../../../../shared/exception/ErrorCode";
import PortalContext from "../../../PortalContext";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {PdfPassword, PdfPasswordInterface} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Helper from "../../../../shared/components/Helper";
import Translate from "../../../../shared/translation/Translate";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import AbstractDialog, {
	AbstractDialogProperties,
	AbstractDialogState
} from "../../../../shared/dialog/components/AbstractDialog";

export interface PasswordDialogProperties extends AbstractDialogProperties {
	context: PortalContext;
	close?: () => void;
	portalDocument: PortalDocument;
	exception?: Exception;
}

export interface PasswordDialogState extends AbstractDialogState {
	password: string;
	hasError: boolean;
}

export class PasswordDialogDefinition extends AbstractDialog<PasswordDialogProperties, PasswordDialogState> {
	/**
	 * Initialize an AbstractDialog component instance
	 */
	constructor(props: Readonly<PasswordDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			password: "",
			hasError: false
		}
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		if (!FunctionHelper.exists(this.state.password) || FunctionHelper.isEmpty(this.state.password)) {
			this.setState({
				hasError: true
			});
			return;
		}

		await super.submit();
		let portalDocument: PortalDocument = this.props.portalDocument;
		let passwordType: PdfPassword = PdfPassword.fromJson({
			permission: this.state.password
		} as PdfPasswordInterface);

		try {
			portalDocument = await this.props.context.documentManager.updateSecurity(
				portalDocument.getSourceDocumentId()!, passwordType
			);

			let error: Exception | undefined = portalDocument.getError();
			if (FunctionHelper.exists(errorCode) && error?.code === ErrorCode.PDF_PASSWORD_OWNER) {
				passwordType.permission = "";
				await this.props.context.documentManager.updateSecurity(
					portalDocument.getSourceDocumentId()!, passwordType
				);

				throw error;
			}
		} catch (exception: any) {
			this.setState({
				exception: exception,
				submitted: false
			});
			return;
		}

		this.hide();
	}

	/**
	 * Handle closing/hiding the dialog
	 */
	protected hide(close?: boolean): void {
		this.setState(
			{
				submitted: false,
				exception: undefined
			}
		);

		if (close && FunctionHelper.exists(this.props.close)) {
			this.props.close!();
		} else {
			this.props.hide();
		}
	}

	/**
	 * Render dialog errors
	 */
	protected renderError(): ReactNode {
		let error: Exception | undefined = this.state.exception || this.props.exception;

		if (!FunctionHelper.exists(error)) {
			return null;
		}

		return (
			<div className="p-col-12">
				<MessageBox {...this.props} type={MessageType.DANGER} stacktrace={
					<>
						<p>{error!.message} ({error!.code})</p>
						<pre>{error!.stack}</pre>
					</>
				}>
					{error!.message}
				</MessageBox>
			</div>
		);
	}

	/**
	 * Render the dialog header
	 */
	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-unlocked small"}/>
					<Translate {...this.props} data="password.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide(true)}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.submit")} disabled={!this.isValid()}
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
							className="primary-button" onClick={() => this.submit()}/>
					{this.state.submitted ?
						<Button label={Translate.value("buttons.close")} icon="icon icon-close"
								onClick={() => this.hide(true)} className="dialog-button"/> :
						<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
								onClick={() => this.hide(true)} className="dialog-button"/>
					}
				</div>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		return !this.state.hasError && !this.state.submitted;
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["password-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={() => this.hide(true)}
					resizable={false} closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.password.general"} namespace={"help"} markdown={true}/>
					</Helper>
					<fieldset className="p-col-12 p-grid">
						<div className="dialog-message">
							<Translate {...this.props} data={"password.message"} namespace={"dialog"}
									   values={{filename: this.props.portalDocument.getFileName()}}/>
						</div>
					</fieldset>
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor="control-permission-password">
								<Translate {...this.props} data="password.permission" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<Password className={this.state.hasError ? "p-invalid" : ""}
										  tooltip={this.state.hasError ?
											  Translate.value("errors.fields.set", {ns: "message"}) : ""
										  }
										  id="control-permission-password" feedback={false} toggleMask={true}
										  appendTo={document.getElementById("root")} onChange={
									(e) => this.onChange(e.currentTarget.value)
								}/>
								{this.renderFieldError(
									this.state.hasError ? Translate.value(
										"errors.fields.set", {ns: "message"}
									) : ""
								)}
							</div>
						</div>
					</fieldset>
				</div>
			</Dialog>
		);
	}

	private onChange(value: any): void {
		if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
			this.setState({
				hasError: true
			});
		} else if (this.state.hasError) {
			this.setState({
				hasError: false
			});
		}

		this.setState({
			password: value
		});
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (event.key.toLowerCase() === "enter" && this.isValid() && !this.state.submitted) {
			await this.submit();
		}
	}
}

const PasswordDialog = React.memo(PasswordDialogDefinition,
	(prevProps: Readonly<PasswordDialogProperties>, nextProps: Readonly<PasswordDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default PasswordDialog;